import React from 'react';

import {
    useEffect,
    useMemo,
    useRef,
} from 'react';

import {
    Route,
} from "react-router-dom";

import { Helmet } from "react-helmet";

import {
    Box,
    CssBaseline,
} from '@material-ui/core';

import {
    makeStyles,
} from '@material-ui/core/styles';

// Interface avec Google Tag Manager
import TagManager from 'react-gtm-module';


// Hook utilisé pour dialoguer avec le serveur
import { useDataLoader } from 'PathCore/contexts/DataLoaderContext.jsx';

// Composants propres au projet
import { Header } from "PathWWW/components/Header.jsx";
import { Footer } from "PathWWW/components/Footer.jsx";
import { MainBreadcrumb } from "PathWWW/components/MainBreadcrumb.jsx";
import { FAB } from "PathWWW/components/FAB.jsx";
import { ContentFactory } from 'PathContent/components/ContentFactory.jsx';
import { CookieDialog } from "PathWWW/components/CookieDialog.jsx";

// Importation du fichier de configuration propre au projet
import {
    GOOGLE_TAG_MANAGER_ID,
} from "PathConfig/components/ProjectSettings.jsx";





// Style propre au composant
const useBodyStyles = makeStyles((theme) => ({
    main: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '70vh',
    },
}));




//
// Composant représentant la page entière (le 'body') du site web. Il contient notamment :
// - l'entête et le menu
// - le corps de la page avec les éléments de routage
// - le footer
//
export const Body = (props) => {


    // On récupère les propriétés
    const { history, location, match, staticContext } = props;


    ///////////////////////////////////////////
    //                                       //
    //          Gestion des données          //
    //                                       //
    ///////////////////////////////////////////

    // Hook permettant de demander les données au serveur, en fonction de l'URL de la page demandée
    // Ce hook est lié au contexte 'DataLoaderContext'
    const { data, fetchData } = useDataLoader();

    // Url associée aux données courantes
    const currentDataUrl = useRef();



    //////////////////////////////////////////////
    //                                          //
    //          Gestion des événements          //
    //                                          //
    //////////////////////////////////////////////

    // Hook appelé à chaque changement de page : on demande au serveur les données
    // associées à l'URL actuelle
    useEffect(() => {

        // Est-on côté client ?
        if(__isBrowser__) {
            // OUI : est-ce que l'URL courante (correspondant aux données courantes) a changé ?
            if(currentDataUrl.current !== location.pathname) {
                // OUI : on demande les données associées à la nouvelle URL
                fetchData(location.pathname);

                // On met à jour l'URL courante
                currentDataUrl.current = location.pathname;
            }

            // Il y a-t-il une ancre dans l'URL ?
            if(!location.hash) {
                // NON : on se replace en haut de la page
                window.scrollTo(0, 0);
            }
        }

    }, [location.pathname]);


    // Hook exécuté au chargement de la page
    useEffect(() => {

        if(__isBrowser__ && !__dev_mode__) {
            // Initialisation de Google Tag Manager
            TagManager.initialize({
                gtmId: __GOOGLE_TAG_MANAGER_ID__,
            });
        }

    }, []);



    ////////////////////////////////////////
    //                                    //
    //          Gestion du style          //
    //                                    //
    ////////////////////////////////////////

    // On crée le style
    const classes = useBodyStyles();



    //////////////////////////////////////////
    //                                      //
    //          Rendu du composant          //
    //                                      //
    //////////////////////////////////////////

    return (
        <>
            {/* Initialisation du style avec des valeurs par défaut */}
            <CssBaseline />


            {/* Mise en place des balises 'meta' et autres, via 'react-helmet' */}
            <Helmet>
                <html lang="fr" />
                {data && data.header && <title>{data.header.title}</title>}
                {data && data.header && <meta name="description" content={data.header.description} />}
            </Helmet>


            {/* Partie HEADER */}
            {useMemo(() => (
                <Route
                    path="/"
                    exact={false}
                    render={(propsRoute) => (
                        <Header
                            {...propsRoute}
                            header={data.header}
                            menu={data.menu}
                        />
                    )}
                />
            ), [
                data.header,
                data.menu,
            ])}


            {/* Partie MAIN */}
            <Box
                component='main'
                className={classes.main}
            >
                {/* Fil d'ariane, présent sur toutes les pages */}
                {data.breadcrumb &&
                    <MainBreadcrumb
                        breadcrumb={data.breadcrumb}
                    />
                }

                {/* Toutes les URLs sont redirigées vers le composant 'ContentFactory' qui s'occupera du contenu */}
                {useMemo(() => (
                    <Route
                        path="/"
                        exact={false}
                        render={(propsRoute) =>
                            <ContentFactory
                                {...propsRoute}
                                content={data.lst_contents}
                            />
                        }
                    />
                ), [
                    data.lst_contents,
                ])}

                {/* Bouton d'action flottant, présent sur toutes les pages */}
                <FAB />
            </Box>


            {/* Partie FOOTER */}
            <Box
                component='footer'
            >
                {useMemo(() => (
                    <Route
                        path="/"
                        exact={false}
                        render={(propsRoute) => (
                            <Footer {...propsRoute} footer={data.footer} />
                        )}
                    />
                ), [
                    data.footer
                ])}
            </Box>


            {/* Boite de dialogue de configuration des cookies */}
            <CookieDialog />
        </>
    );
}
